import { useEffect, useRef, useState } from 'react';
import { Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../lib/utils';
import { cardClass, borderLight, primaryText, secondaryText, tertiaryText, extraSmallTextSize, smallTextSize } from '../../styling/stylingHelper';
import { currencyFormatter } from '../../services/helperService';
import { stockChange, type SideStockAdjustment } from '../../utils/inventoryAdjustedCostPercent';

const formatPct = (n: number) => `${n.toLocaleString(undefined, { maximumFractionDigits: 1 })}%`;

interface Props {
  percent: number;
  stock: SideStockAdjustment;
  /** Inline after a split-legend row instead of its own line. */
  compact?: boolean;
}

/** Dashboard cost % secondary line: stock-adjusted cost % with a tap-to-open breakdown. */
export function StockAdjustedCost({ percent, stock, compact = false }: Props) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (!open) return;
    const close = (event: MouseEvent) => { if (!ref.current?.contains(event.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  const dates = (days: string[]) => days.map(day => new Date(`${day}T12:00:00Z`)
    .toLocaleDateString(i18n.language, { day: 'numeric', month: 'short' })).join(', ');
  const signed = (n: number) => `${n > 0 ? '+' : ''}${currencyFormatter.format(n)}`;
  const rows: [string, string][] = [
    [`${t('dashboard.stockAdjusted.openingStock')} (${dates(stock.startCountDays)})`, currencyFormatter.format(stock.stockStart)],
    [`${t('dashboard.stockAdjusted.closingStock')} (${dates(stock.endCountDays)})`, currencyFormatter.format(stock.stockEnd)],
    ...(stock.estimated ? [[t('dashboard.stockAdjusted.adjustments'), signed(stock.adjustment)] as [string, string]] : []),
    [t('dashboard.stockAdjusted.stockChange'), signed(stockChange(stock))],
  ];

  return (
    <span ref={ref} className={cn('relative inline-flex items-center gap-1 flex-wrap', compact ? '' : smallTextSize)} onClick={event => event.stopPropagation()} onKeyDown={event => event.stopPropagation()}>
      {compact && <span className={tertiaryText}>·</span>}
      <span className={cn(tertiaryText, 'font-medium')}>{compact ? t('dashboard.stockAdjusted.short') : t('dashboard.stockAdjusted.label')}</span>
      <span className={cn(primaryText, 'font-semibold')}>{formatPct(percent)}</span>
      <button
        type="button"
        aria-label={t('dashboard.stockAdjusted.info')}
        onClick={() => setOpen(value => !value)}
        aria-expanded={open}
        className={cn('inline-flex items-center gap-1 p-0.5 rounded', extraSmallTextSize, tertiaryText)}
      >
        <Info className="w-3.5 h-3.5" />
        {stock.estimated && <span>{t('dashboard.stockAdjusted.estimated')}</span>}
      </button>
      {open && (
        <span className={cn(cardClass, borderLight, 'absolute left-0 top-full mt-1 z-20 w-72 max-w-[calc(100vw-2rem)] border shadow-lg p-3 flex flex-col gap-1.5', extraSmallTextSize)}>
          <span className={cn(primaryText, 'font-semibold')}>{t('dashboard.stockAdjusted.label')}</span>
          {rows.map(([label, value]) => (
            <span key={label} className="flex justify-between gap-3">
              <span className={secondaryText}>{label}</span>
              <span className={cn(primaryText, 'font-medium whitespace-nowrap')}>{value}</span>
            </span>
          ))}
        </span>
      )}
    </span>
  );
}
